'use client';

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useReducer } from 'react';

import { MessageService } from '../services/MessageService';
import { ThreadService } from '../services/ThreadService';
import { OptimisticMessage, SendThreadMessagePayload, Thread, ThreadMessage, UserInfo } from '../types';

// Context state
interface MessageContextState {
  thread: Thread | null;
  messages: ThreadMessage[];
  optimisticMessages: OptimisticMessage[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  sendingMessage: boolean;
  replyingTo: ThreadMessage | null;
  selectedMessages: Set<string>;
  currentUser: UserInfo | null;
  deletingMessages: Set<string>;
}

// Context actions
type MessageContextAction =
  | { type: 'SET_THREAD'; thread: Thread | null }
  | { type: 'SET_MESSAGES'; messages: ThreadMessage[]; hasMore: boolean }
  | { type: 'ADD_MESSAGE'; message: ThreadMessage }
  | { type: 'ADD_OPTIMISTIC_MESSAGE'; message: OptimisticMessage }
  | { type: 'REMOVE_OPTIMISTIC_MESSAGE'; tempId: string }
  | { type: 'UPDATE_MESSAGE'; messageId: string; message: ThreadMessage }
  | { type: 'DELETE_MESSAGE'; messageId: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_LOADING_MORE'; loadingMore: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_SENDING_MESSAGE'; sending: boolean }
  | { type: 'SET_REPLYING_TO'; message: ThreadMessage | null }
  | { type: 'TOGGLE_MESSAGE_SELECTION'; messageId: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_CURRENT_USER'; user: UserInfo | null }
  | { type: 'START_DELETE'; messageId: string }
  | { type: 'END_DELETE'; messageId: string };

// Reducer
function messageReducer(state: MessageContextState, action: MessageContextAction): MessageContextState {
  switch (action.type) {
    case 'SET_THREAD':
      return { ...state, thread: action.thread };

    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.messages,
        hasMore: action.hasMore,
        loading: false,
        loadingMore: false,
      };

    case 'ADD_MESSAGE': {
      // Check if message already exists to prevent duplicates
      const messageExists = state.messages.some((m: any) => m._id === action.message._id);
      if (messageExists) {
        return state;
      }
      return {
        ...state,
        messages: [...state.messages, action.message],
      };
    }
    case 'ADD_OPTIMISTIC_MESSAGE':
      return {
        ...state,
        optimisticMessages: [...state.optimisticMessages, action.message],
      };

    case 'REMOVE_OPTIMISTIC_MESSAGE':
      return {
        ...state,
        optimisticMessages: state.optimisticMessages.filter((m) => m._tempId !== action.tempId),
      };

    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((m) => (m._id === action.messageId ? action.message : m)),
      };

    case 'DELETE_MESSAGE':
      return {
        ...state,
        messages: state.messages.filter((m) => m._id !== action.messageId),
      };

    case 'SET_LOADING':
      return { ...state, loading: action.loading };

    case 'SET_LOADING_MORE':
      return { ...state, loadingMore: action.loadingMore };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'SET_SENDING_MESSAGE':
      return { ...state, sendingMessage: action.sending };

    case 'SET_REPLYING_TO':
      return { ...state, replyingTo: action.message };

    case 'TOGGLE_MESSAGE_SELECTION': {
      const newSelection = new Set(state.selectedMessages);
      if (newSelection.has(action.messageId)) {
        newSelection.delete(action.messageId);
      } else {
        newSelection.add(action.messageId);
      }
      return { ...state, selectedMessages: newSelection };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selectedMessages: new Set() };

    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.user };

    case 'START_DELETE': {
      const newDeleting = new Set(state.deletingMessages);
      newDeleting.add(action.messageId);
      return { ...state, deletingMessages: newDeleting };
    }
    case 'END_DELETE': {
      const updatedDeleting = new Set(state.deletingMessages);
      updatedDeleting.delete(action.messageId);
      return { ...state, deletingMessages: updatedDeleting };
    }
    default:
      return state;
  }
}

// Context definition
interface MessageContextValue extends MessageContextState {
  /** The id of the currently authenticated user */
  currentUserId?: string;
  /** Initial message to prefill in composer */
  initialMessage?: string;
  // Thread actions
  loadThread: (threadId: string) => Promise<void>;
  refreshThread: () => Promise<void>;

  // Message actions
  loadMessages: (offset?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  sendMessage: (threadId: string, text: string, contentIds?: string[], replyToId?: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  updateMessage: (messageId: string, updates: Partial<SendThreadMessagePayload>) => Promise<void>;

  // UI actions
  setReplyingTo: (message: ThreadMessage | null) => void;
  toggleMessageSelection: (messageId: string) => void;
  clearSelection: () => void;

  // Thread management
  toggleMute: () => Promise<void>;
  addMembers: (userIds: string[]) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  leaveThread: () => Promise<void>;
  updateThreadInfo: (updates: any) => Promise<void>;
}

const MessageContext = createContext<MessageContextValue | undefined>(undefined);

// Provider props
interface MessageProviderProps {
  children: ReactNode;
  threadId: string;
  currentUser?: UserInfo;
  isBusinessThread?: boolean;
  initialMessage?: string;
}

// Provider component
export function MessageProvider({
  children,
  threadId,
  currentUser,
  isBusinessThread = true,
  initialMessage,
}: MessageProviderProps) {
  const messageService = MessageService.getInstance();
  const threadService = ThreadService.getInstance();

  const [state, dispatch] = useReducer(messageReducer, {
    thread: null,
    messages: [],
    optimisticMessages: [],
    loading: true,
    loadingMore: false,
    hasMore: true,
    error: null,
    sendingMessage: false,
    replyingTo: null,
    deletingMessages: new Set(),
    selectedMessages: new Set(),
    currentUser: currentUser || null,
  });

  // Load thread details
  const loadThread = useCallback(
    async (threadId: string) => {
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const thread = await threadService.getThread(threadId);
        dispatch({ type: 'SET_THREAD', thread });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
      }
    },
    [threadService],
  );

  // Load messages
  const loadMessages = useCallback(
    async (offset: number = 0) => {
      if (state.loadingMore && offset > 0) return;

      try {
        dispatch({
          type: offset > 0 ? 'SET_LOADING_MORE' : 'SET_LOADING',
          [offset > 0 ? 'loadingMore' : 'loading']: true,
        });

        const response = await messageService.getThreadMessages(
          threadId,
          { limit: 20, offset },
          isBusinessThread,
        );

        // Extract thread data from the response on initial load
        if (response.data.thread && offset === 0) {
          dispatch({ type: 'SET_THREAD', thread: response.data.thread });
        }

        const messages = offset > 0 ? [...response.data.messages, ...state.messages] : response.data.messages;

        dispatch({
          type: 'SET_MESSAGES',
          messages,
          hasMore: response.data.messages.length === 20,
        });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
      }
    },
    [threadId, state.loadingMore, messageService, isBusinessThread],
  );

  // Send message
  const sendMessage = useCallback(
    async (threadId: string, text: string, contentIds: string[] = [], replyToId?: string) => {
      if (!text.trim() && contentIds.length === 0) return;

      let optimistic: any;
      try {
        dispatch({ type: 'SET_SENDING_MESSAGE', sending: true });

        // Create optimistic message
        optimistic = messageService['createOptimisticMessage'](
          threadId,
          text,
          contentIds,
          state.currentUser || undefined,
          replyToId,
        );

        dispatch({ type: 'ADD_OPTIMISTIC_MESSAGE', message: optimistic });

        // Send message
        const message = await messageService.sendMessage(threadId, text, contentIds, replyToId);

        // Remove optimistic and add real message
        dispatch({ type: 'REMOVE_OPTIMISTIC_MESSAGE', tempId: optimistic._tempId });
        dispatch({ type: 'ADD_MESSAGE', message });

        // Clear reply state if this was a reply
        if (replyToId) {
          dispatch({ type: 'SET_REPLYING_TO', message: null });
        }

        // TODO: WebSocket will handle real-time updates
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
        // Mark optimistic message as failed instead of removing
        if (optimistic) {
          dispatch({
            type: 'UPDATE_MESSAGE',
            messageId: optimistic._tempId,
            message: { ...optimistic, status: 'failed' },
          });
        }
      } finally {
        dispatch({ type: 'SET_SENDING_MESSAGE', sending: false });
      }
    },
    [messageService],
  );

  // Delete message
  const deleteMessage = useCallback(
    async (messageId: string) => {
      dispatch({ type: 'START_DELETE', messageId });
      try {
        await messageService.deleteMessage(messageId, threadId, isBusinessThread);
        dispatch({ type: 'DELETE_MESSAGE', messageId });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
        // Show error toast or notification
        console.error('Failed to delete message:', error);
      } finally {
        dispatch({ type: 'END_DELETE', messageId });
      }
    },
    [threadId, messageService, isBusinessThread],
  );

  // Update message
  const updateMessage = useCallback(
    async (messageId: string, updates: Partial<SendThreadMessagePayload>) => {
      try {
        const updated = await messageService.updateMessage(messageId, threadId, updates);
        dispatch({ type: 'UPDATE_MESSAGE', messageId, message: updated });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
      }
    },
    [threadId, messageService],
  );

  // Thread management actions
  const toggleMute = useCallback(async () => {
    if (!state.thread) return;

    try {
      const newMuteState = !state.thread.isMuted;
      await threadService.toggleMute(threadId, newMuteState);
      dispatch({
        type: 'SET_THREAD',
        thread: { ...state.thread, isMuted: newMuteState },
      });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', error: error.message });
    }
  }, [threadId, state.thread, threadService]);

  const addMembers = useCallback(
    async (userIds: string[]) => {
      try {
        await threadService.addMembers(threadId, userIds);
        // Reload thread to get updated participants
        await loadThread(threadId);
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
      }
    },
    [threadId, threadService, loadThread],
  );

  const removeMember = useCallback(
    async (userId: string) => {
      try {
        await threadService.removeMember(threadId, userId);
        // Update local state
        if (state.thread?.participants) {
          const updated = {
            ...state.thread,
            participants: state.thread.participants.filter((p) => p.Id !== userId),
          };
          dispatch({ type: 'SET_THREAD', thread: updated });
        }
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
      }
    },
    [threadId, state.thread, threadService],
  );

  const leaveThread = useCallback(async () => {
    if (!state.currentUser) return;

    try {
      await threadService.leaveThread(threadId, state.currentUser.Id);
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', error: error.message });
    }
  }, [threadId, state.currentUser, threadService]);

  const updateThreadInfo = useCallback(
    async (updates: any) => {
      try {
        const updated = await threadService.updateThreadInfo(threadId, updates);
        dispatch({ type: 'SET_THREAD', thread: updated });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', error: error.message });
      }
    },
    [threadId, threadService],
  );

  // UI actions
  const setReplyingTo = useCallback((message: ThreadMessage | null) => {
    dispatch({ type: 'SET_REPLYING_TO', message });
  }, []);

  const toggleMessageSelection = useCallback((messageId: string) => {
    dispatch({ type: 'TOGGLE_MESSAGE_SELECTION', messageId });
  }, []);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  // Initial load - Only load messages, which will also fetch thread data
  useEffect(() => {
    if (threadId) {
      loadMessages();
    }
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Combine real and optimistic messages
  const allMessages = [...state.messages, ...state.optimisticMessages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const currentUserId = state.currentUser?.Id;

  // Create loadMore function
  const loadMore = useCallback(() => {
    const currentMessageCount = state.messages.length;
    return loadMessages(currentMessageCount);
  }, [state.messages.length, loadMessages]);

  const value: MessageContextValue = {
    ...state,
    currentUserId,
    initialMessage,
    messages: allMessages,
    loadThread,
    refreshThread: () => loadMessages(0), // Load from offset 0 to get thread data
    loadMessages,
    loadMore,
    sendMessage,
    deleteMessage,
    updateMessage,
    setReplyingTo,
    toggleMessageSelection,
    clearSelection,
    toggleMute,
    addMembers,
    removeMember,
    leaveThread,
    updateThreadInfo,
  };

  return <MessageContext.Provider value={value}>{children}</MessageContext.Provider>;
}

// Hook to use the context
export function useMessageContext() {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessageContext must be used within MessageProvider');
  }
  return context;
}
